import '../App.css';
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";
import TrainpaneBetaBanner from "../components/TrainpaneBetaBanner";

const base_url = process.env.NODE_ENV === "production" ? "https://hooks-28xd.onrender.com" : "http://localhost:4000";

const SPORT_COLORS = {
    Run: "#ff6b35",
    Ride: "#00d4ff",
    Swim: "#4ecdc4",
    WeightTraining: "#a855f7",
    Workout: "#a855f7",
    Walk: "#f59e0b",
    Hike: "#84cc16",
};

function formatDistance(meters) {
    if (!meters) return null;
    return `${(meters / 1609.34).toFixed(2)} mi`;
}

function formatDuration(seconds) {
    if (!seconds) return null;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m ${s}s`;
}

function formatPace(metersPerSecond) {
    if (!metersPerSecond) return null;
    const minutesPerMile = 26.8224 / metersPerSecond;
    const min = Math.floor(minutesPerMile);
    const sec = Math.round((minutesPerMile - min) * 60);
    return `${min}:${String(sec).padStart(2, "0")}/mi`;
}

function formatSpeed(metersPerSecond) {
    if (!metersPerSecond) return null;
    return `${(metersPerSecond * 2.23694).toFixed(1)} mph`;
}

function formatDate(dateStr) {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function formatWeekRange(start, end) {
    if (!start || !end) return "";
    const s = new Date(start).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const e = new Date(end).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${s} – ${e}`;
}

function totalSeconds(activities) {
    return activities.reduce((sum, a) => sum + (a.moving_time || 0), 0);
}

function totalMeters(activities) {
    return activities.reduce((sum, a) => sum + (a.distance || 0), 0);
}

function groupBySport(activities) {
    const groups = {};
    for (const a of activities) {
        const key = a.sport_type || a.type || "Other";
        if (!groups[key]) groups[key] = [];
        groups[key].push(a);
    }
    return groups;
}

const Home = () => {
    const navigate = useNavigate();
    const [activities, setActivities] = useState([]);
    const [weekRange, setWeekRange] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [queueCount, setQueueCount] = useState(null);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        axios.get(`${base_url}/strava/week-activities`)
            .then(({ data }) => {
                if (data.success) {
                    setActivities(data.activities);
                    setWeekRange({ start: data.weekStart, end: data.weekEnd });
                } else {
                    setError(data.message || "No data returned");
                }
            })
            .catch(e => setError(e.response?.data?.message || e.message))
            .finally(() => setLoading(false));

        authgear.refreshAccessTokenIfNeeded()
            .then(() => axios.get(`${base_url}/bluesky/queue`, {
                headers: { Authorization: `Bearer ${authgear.accessToken}` }
            }))
            .then(({ data }) => setQueueCount(data.length))
            .catch(() => {});
    }, [navigate]);

    const totalTime = totalSeconds(activities);
    const totalDist = totalMeters(activities);

    const card = {
        background: "#1a1a2e",
        border: "1px solid #2a2a4a",
        borderRadius: "10px",
        padding: "18px 20px",
    };

    const statCard = (label, value, sub, color = "#00d4ff") => (
        <div style={{ ...card, textAlign: "center", minWidth: "110px", flex: "1" }}>
            <div style={{ fontSize: "22px", fontWeight: "bold", color }}>{value || "—"}</div>
            {sub && <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>{sub}</div>}
            <div style={{ fontSize: "11px", color: "#555", marginTop: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
        </div>
    );

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 20px" }}>

                <TrainpaneBetaBanner cardStyle={card} />

                {queueCount !== null && (
                    <div
                        onClick={() => navigate("/bluesky")}
                        style={{ ...card, marginBottom: "24px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                    >
                        <span style={{ color: "#aaa", fontSize: "13px" }}>Bluesky message queue</span>
                        <span style={{ color: "#00d4ff", fontWeight: "bold", fontSize: "15px" }}>
                            {queueCount} {queueCount === 1 ? "message" : "messages"} queued
                        </span>
                    </div>
                )}

                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "8px" }}>
                    <h2 style={{ color: "#00d4ff", margin: 0 }}>This Week</h2>
                    <span style={{ color: "#555", fontSize: "13px" }}>{formatWeekRange(weekRange.start, weekRange.end)}</span>
                </div>

                {loading ? (
                    <p style={{ color: "#555", textAlign: "center", marginTop: "60px" }}>Loading...</p>
                ) : error ? (
                    <div style={{ background: "#2a1a1a", border: "1px solid #4a2a2a", borderRadius: "8px", padding: "16px", color: "#f88", fontSize: "13px" }}>
                        Error: {error}
                    </div>
                ) : activities.length === 0 ? (
                    <div style={{ ...card, textAlign: "center", padding: "40px", color: "#555" }}>
                        No activities this week yet.
                    </div>
                ) : (
                    <>
                        {/* Summary row */}
                        <div style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
                            {statCard("Activities", activities.length, null, "#00d4ff")}
                            {totalDist > 0 && statCard("Total Distance", formatDistance(totalDist), null, "#ff6b35")}
                            {statCard("Total Time", formatDuration(totalTime), null, "#4ecdc4")}
                        </div>

                        {/* Per-sport breakdown */}
                        {Object.entries(groupBySport(activities)).map(([sport, acts]) => {
                            const color = SPORT_COLORS[sport] || "#888";
                            const dist = totalMeters(acts);
                            const time = totalSeconds(acts);
                            const isRun = sport === "Run";
                            const isRide = sport === "Ride";
                            return (
                                <div key={sport} style={{ ...card, marginBottom: "16px" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: color }} />
                                            <span style={{ fontWeight: "bold", color, fontSize: "15px" }}>{sport}</span>
                                            <span style={{ color: "#555", fontSize: "12px" }}>{acts.length} {acts.length === 1 ? "activity" : "activities"}</span>
                                        </div>
                                        <div style={{ display: "flex", gap: "16px", fontSize: "13px", color: "#aaa" }}>
                                            {dist > 0 && <span>{formatDistance(dist)}</span>}
                                            <span>{formatDuration(time)}</span>
                                        </div>
                                    </div>

                                    {/* Individual activities */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                        {acts.map(a => (
                                            <div key={a.id} style={{ background: "#0d0d1a", borderRadius: "6px", padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
                                                <div>
                                                    <div style={{ fontSize: "13px", color: "#ddd" }}>{a.name}</div>
                                                    <div style={{ fontSize: "11px", color: "#555", marginTop: "2px" }}>{formatDate(a.start_date)}</div>
                                                </div>
                                                <div style={{ display: "flex", gap: "14px", fontSize: "12px", color: "#888" }}>
                                                    {a.distance > 0 && <span>{formatDistance(a.distance)}</span>}
                                                    {a.moving_time > 0 && <span>{formatDuration(a.moving_time)}</span>}
                                                    {isRun && a.average_speed > 0 && <span style={{ color }}>{formatPace(a.average_speed)}</span>}
                                                    {isRide && a.average_speed > 0 && <span style={{ color }}>{formatSpeed(a.average_speed)}</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
};

export default Home;
